import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate, useParams, Link } from 'react-router-dom';
import type { RootState } from '../../store';
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import type { ReRegisterTeamRequest, Team, Division } from '@sulis-liiga/common';
import leagueService from '../../services/leagueService';
import ReRegistrationForm from '../../components/team/ReRegistrationForm';

/**
 * ReRegistrationPage
 *
 * Allows players to re-register their existing teams for a new season,
 * or navigate to form a completely new team.
 */
const ReRegistrationPage: React.FC = () => {
  const navigate = useNavigate();
  const { leagueId, seasonId } = useParams<{ leagueId: string; seasonId: string }>();
  const { user } = useSelector((state: RootState) => state.auth);

  const [previousTeams, setPreviousTeams] = useState<Team[]>([]);
  const [availableDivisions, setAvailableDivisions] = useState<Division[]>([]);
  const [loading, setLoading] = useState(false);
  const [fetchLoading, setFetchLoading] = useState(true);
  const [success, setSuccess] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      if (!user || !seasonId || !leagueId) return;

      setFetchLoading(true);
      try {
        // We'd normally fetch previous season teams for this user here
        // For now, initialize with empty arrays
        setPreviousTeams([]);
        setAvailableDivisions([]);
      } catch (err) {
        setError('Failed to load registration data');
      } finally {
        setFetchLoading(false);
      }
    };

    fetchData();
  }, [user, seasonId, leagueId]);

  const handleReRegister = async (teamId: string, newSeasonId: string, divisionId?: string) => {
    setLoading(true);
    setError(null);

    try {
      const request: ReRegisterTeamRequest = {
        teamId,
        newSeasonId,
        newDivisionId: divisionId,
      };

      const result = await leagueService.reRegisterTeam(request);

      setSuccess(
        `Successfully re-registered "${result.teamName}" for the new season in ${result.divisionName}.`,
      );
      // Navigate to teams list after a short delay
      setTimeout(() => {
        navigate('/teams');
      }, 2000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to re-register team');
    } finally {
      setLoading(false);
    }
  };

  const handleFormNewTeam = () => {
    // The live team-creation flow lives on the Teams page (/teams/create is not a
    // route). Send the player there to start a new singles/pair team.
    navigate('/teams');
  };

  if (fetchLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <svg
          className="animate-spin h-10 w-10 text-primary-600"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
        >
          <circle
            className="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="4"
          ></circle>
          <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          ></path>
        </svg>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Breadcrumb */}
      <nav className="flex mb-6" aria-label="Breadcrumb">
        <ol className="flex items-center space-x-2">
          <li>
            <Link to="/teams" className="text-gray-400 hover:text-gray-500 text-sm">
              Teams
            </Link>
          </li>
          <li className="text-gray-400">/</li>
          <li>
            <span className="text-gray-700 text-sm font-medium">Re-registration</span>
          </li>
        </ol>
      </nav>

      {/* Page header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Team Re-registration</h1>
        <p className="mt-1 text-sm text-gray-500">
          Register your team for the new season or form a new team.
        </p>
      </div>

      {/* Success message */}
      {success && (
        <div className="mb-6 rounded-md bg-green-50 p-4 border border-green-200">
          <p className="text-sm text-green-800">{success}</p>
        </div>
      )}

      {/* Error message */}
      {error && (
        <div className="mb-6 rounded-md bg-danger-50 p-4 border border-danger-200">
          <p className="text-sm text-danger-800">{error}</p>
        </div>
      )}

      {/* Form */}
      {!success && seasonId && (
        <div className="bg-white shadow rounded-lg p-6">
          <ReRegistrationForm
            previousTeams={previousTeams}
            availableDivisions={availableDivisions}
            onSubmit={handleReRegister}
            onFormNewTeam={handleFormNewTeam}
            loading={loading}
            newSeasonId={seasonId}
          />
        </div>
      )}
    </div>
  );
};

export default ReRegistrationPage;
